import React from "react";
import { Sparkles, Users, Target, MapPin, Mail } from "lucide-react";

export default function AboutUs() {
  return (
    <div
      className="
        relative min-h-screen text-white
        bg-transparent
        px-4 sm:px-6 md:px-10
        pt-4 pb-10
      "
    >

      {/* Updated ambient overlays — navy, not muddy black */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        {/* soft vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050915]/35 via-transparent to-[#050915]/45" />
        {/* top glow */}
        <div className="absolute -top-24 left-[-10%] h-[420px] w-[520px] rounded-full bg-emerald-500/10 blur-3xl" />
        {/* right neutral glow */}
        <div className="absolute -top-10 right-[-15%] h-[360px] w-[520px] rounded-full bg-white/7 blur-3xl" />
        {/* bottom glow */}
        <div className="absolute bottom-[-120px] left-[15%] h-[380px] w-[520px] rounded-full bg-emerald-400/7 blur-3xl" />
        {/* subtle navy wash to keep it bright */}
        <div className="absolute inset-0 bg-[#060b18]/25" />
      </div>

      <div className="max-w-6xl mx-auto w-full mt-6 md:mt-8">
        {/* Title / intro */}
        <div
          className="
            relative rounded-3xl overflow-hidden
            border border-white/14
            bg-[#0b1220]/55 backdrop-blur-2xl
            shadow-[0_20px_90px_rgba(0,0,0,0.55)]
          "
        >
          {/* surface sheen like Anime Details */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.07] via-transparent to-black/10" />
            <div className="absolute inset-x-0 top-0 h-px bg-white/12" />
          </div>

          <div className="relative px-5 sm:px-7 py-6 sm:py-7">
            <div className="relative flex items-start gap-4">
              <div className="h-11 w-11 rounded-2xl border border-white/12 bg-white/[0.06] grid place-items-center">
                <Sparkles className="h-5 w-5 text-emerald-200/90" />
              </div>

              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-gray-50">
                  About <span className="text-emerald-300">AnimeHouse</span>
                </h1>
                <p className="mt-2 text-sm sm:text-base text-gray-200/85 leading-relaxed">
                  A modern home for anime fans — built for discovery, discussion, and community.
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Pill>Discover</Pill>
                  <Pill>Discuss</Pill>
                  <Pill>Collect</Pill>
                  <Pill>Create</Pill>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="relative h-px bg-white/10" />

          {/* Cards */}
          <div className="relative p-5 sm:p-7">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
              {/* Section 1 */}
              <InfoCard
                icon={<Sparkles className="h-4 w-4 text-emerald-200/90" />}
                title="What Is Anime?"
              >
                Anime isn’t just cartoons — it’s a storytelling medium that blends{" "}
                <span className="text-gray-50 font-semibold">breathtaking art</span>,{" "}
                <span className="text-gray-50 font-semibold">deep emotion</span>, and{" "}
                <span className="text-gray-50 font-semibold">big ideas</span>. From slice-of-life calm
                to reality-bending sci-fi, anime turns imagination into something you can feel. No matter
                your taste —{" "}
                <span className="text-gray-50 font-semibold">shonen</span>,{" "}
                <span className="text-gray-50 font-semibold">shojo</span>,{" "}
                <span className="text-gray-50 font-semibold">isekai</span>,{" "}
                <span className="text-gray-50 font-semibold">seinen</span> — there’s always a story
                waiting for you.
              </InfoCard>

              {/* Section 2 */}
              <InfoCard
                icon={<Target className="h-4 w-4 text-emerald-200/90" />}
                title="Our Mission"
              >
                At <span className="text-emerald-300 font-semibold">AnimeHouse</span>, we’re building a
                space where fans can{" "}
                <span className="text-gray-50 font-semibold">explore</span>,{" "}
                <span className="text-gray-50 font-semibold">share</span>, and{" "}
                <span className="text-gray-50 font-semibold">connect</span>. Whether you’re brand new
                or a lifelong otaku, our goal is simple: make your experience feel{" "}
                <span className="text-gray-50 font-semibold">clean, fast, and fun</span> — with the
                community energy turned all the way up.
              </InfoCard>

              {/* Section 3 */}
              <InfoCard
                icon={<Users className="h-4 w-4 text-emerald-200/90" />}
                title="Our Vision"
              >
                We’re building more than a website — we’re shaping an{" "}
                <span className="text-gray-50 font-semibold">anime hub</span> that feels alive:
                deep-dive discussions, evolving community spaces, and tools that make anime discovery
                feel effortless. Based in{" "}
                <span className="text-gray-50 font-semibold">California</span>, we’re working toward
                becoming the{" "}
                <span className="text-gray-50 font-semibold">#1 go-to platform</span> for anime lovers
                worldwide.
              </InfoCard>
            </div>

            {/* Contact panel */}
            <div
              className="
                mt-5 sm:mt-6 rounded-3xl overflow-hidden
                border border-white/14
                bg-[#0b1220]/55 backdrop-blur-2xl
                shadow-[0_16px_70px_rgba(0,0,0,0.50)]
              "
            >
              <div className="relative px-5 sm:px-7 py-5 sm:py-6">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-transparent to-white/7" />

                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-50 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-emerald-200/90" />
                      Get in Touch
                    </h3>
                    <p className="mt-1 text-sm sm:text-base text-gray-200/85 leading-relaxed">
                      Questions, suggestions, or you just want to geek out about a series? Send us a message.
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-gray-300/75">
                      <span className="inline-flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        California
                      </span>
                      <span className="text-gray-600">•</span>
                      <span className="inline-flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Community-first
                      </span>
                    </div>
                  </div>

                  <a
                    href="mailto:animehousewebsite@gmail.com"
                    className="
                      inline-flex items-center justify-center gap-2
                      rounded-2xl border border-white/14
                      bg-white/[0.06] hover:bg-white/[0.10]
                      px-4 py-3
                      text-sm font-semibold text-gray-50
                      backdrop-blur-xl
                      shadow-[0_14px_44px_rgba(0,0,0,0.55)]
                      hover:shadow-[0_18px_54px_rgba(0,0,0,0.65)]
                      transition-all
                      focus-visible:outline-none
                      focus-visible:ring-2 focus-visible:ring-emerald-400/40
                    "
                  >
                    <Mail className="h-4 w-4" />
                    animehousewebsite@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <p className="mt-5 text-center text-[11px] text-gray-300/55">
              Built with love for anime — and for the people who never stop talking about it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- small UI bits -------------------- */

function Pill({ children }) {
  return (
    <span
      className="
        inline-flex items-center rounded-full
        border border-white/12
        bg-white/[0.06]
        px-3 py-1.5
        text-[11px] text-gray-100/90
      "
    >
      {children}
    </span>
  );
}

function InfoCard({ icon, title, children }) {
  return (
    <div
      className="
        relative rounded-3xl overflow-hidden
        border border-white/14
        bg-[#0b1220]/55 backdrop-blur-2xl
        shadow-[0_14px_60px_rgba(0,0,0,0.50)]
        p-5 sm:p-6
      "
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.06] via-transparent to-black/10" />

      <div className="relative">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-2xl border border-white/12 bg-white/[0.06] grid place-items-center">
            {icon}
          </div>

          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-extrabold text-emerald-300 tracking-tight">
              {title}
            </h2>
            <div className="mt-3 text-sm sm:text-[15px] text-gray-200/90 leading-relaxed">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
